// src/components/admin/CreateLessonModal.tsx
import React, { useState, useRef, useEffect } from "react";
import { useAddLessonMutation } from "../../services/courses";
import { uploadImage as uploadWorksheetToFirebase } from "../../utils/firebase-client";
import "trix";
import "trix/dist/trix.css";

interface CreateLessonModalProps {
  courseId: string;
  moduleId: string;
  onClose: () => void;
}

/* ===== ADDITION ===== */
interface WorksheetItem {
  title: string;
  path: string;
}
/* ==================== */

export const CreateLessonModal: React.FC<CreateLessonModalProps> = ({
  courseId,
  moduleId,
  onClose,
}) => {
  const [addLesson, { isLoading }] = useAddLessonMutation();

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    hasWorksheet: false,
    worksheetPath: "",
    order: "",
  });

  /* ===== ADDITION ===== */
  const [worksheets, setWorksheets] = useState<WorksheetItem[]>([]);
  const [worksheetTitle, setWorksheetTitle] = useState("");
  /* ==================== */

  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Upload states
  const [uploadingWorksheet, setUploadingWorksheet] = useState(false);
  const [worksheetProgress, setWorksheetProgress] = useState<number | null>(
    null,
  );

  console.log(worksheetProgress)

  const videoInputRef = useRef<HTMLInputElement | null>(null);
  const worksheetInputRef = useRef<HTMLInputElement | null>(null);
  const trixInputRef = useRef<HTMLInputElement | null>(null);
  const trixEditorRef = useRef<HTMLElement | null>(null);

  // Set up Trix editor event listeners
  useEffect(() => {
    const handleTrixChange = (event: any) => {
      setFormData((prev) => ({
        ...prev,
        description: event.target.value,
      }));
    };

    const trixEditor = trixEditorRef.current;
    if (trixEditor) {
      trixEditor.addEventListener("trix-change", handleTrixChange);
    }

    return () => {
      if (trixEditor) {
        trixEditor.removeEventListener("trix-change", handleTrixChange);
      }
    };
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleVideoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("video/")) {
        alert("Please select a valid video file");
        if (videoInputRef.current) videoInputRef.current.value = "";
        return;
      }
      setVideoFile(file);
    }
  };

  const validate = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.title.trim()) {
      newErrors.title = "Lesson title is required";
    } else if (formData.title.trim().length < 3) {
      newErrors.title = "Lesson title must be at least 3 characters";
    }

    if (formData.order && isNaN(Number(formData.order))) {
      newErrors.order = "Order must be a valid number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Worksheet upload (EXTENDED, not replaced)
  const handleWorksheetFile = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!worksheetTitle.trim()) {
      alert("Please enter a worksheet title");
      return;
    }

    setUploadingWorksheet(true);
    setWorksheetProgress(0);

    try {
      let downloadUrl: string = "";
      try {
        downloadUrl = await uploadWorksheetToFirebase(file, "worksheets");
      } catch (innerErr) {
        console.warn(
          "uploadImage failed; attempting direct firebase upload",
          innerErr,
        );
        const { storage } = await import("../../utils/firebase-client");
        const storageRef = storage.ref(`worksheets/${Date.now()}-${file.name}`);
        const uploadTask = storageRef.put(file, { contentType: file.type });
        await new Promise<void>((resolve, reject) => {
          uploadTask.on(
            "state_changed",
            (snapshot: any) => {
              if (snapshot.totalBytes && snapshot.bytesTransferred) {
                const p = Math.round(
                  (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
                );
                setWorksheetProgress(p);
              }
            },
            (err: any) => reject(err),
            async () => {
              const url = await uploadTask.snapshot.ref.getDownloadURL();
              downloadUrl = url;
              resolve();
            },
          );
        });
      }

      if (!downloadUrl) throw new Error("Failed to upload worksheet");

      // Preserve existing behavior
      setFormData((p) => ({ ...p, worksheetPath: downloadUrl }));

      /* ===== ADDITION ===== */
      setWorksheets((prev) => [
        ...prev,
        { title: worksheetTitle.trim(), path: downloadUrl },
      ]);
      setWorksheetTitle("");
      /* ==================== */
    } catch (err: any) {
      console.error("Worksheet upload failed", err);
      alert(err?.message || "Worksheet upload failed");
    } finally {
      setUploadingWorksheet(false);
      setWorksheetProgress(null);
      if (worksheetInputRef.current) worksheetInputRef.current.value = "";
    }
  };

  const removeWorksheet = (index: number) => {
    setWorksheets((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    if (uploadingWorksheet) {
      alert("Please wait until worksheet upload finishes");
      return;
    }

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("courseId", courseId);
      formDataToSend.append("moduleId", moduleId);
      formDataToSend.append("title", formData.title.trim());
      formDataToSend.append("description", formData.description.trim());
      formDataToSend.append("hasWorksheet", String(formData.hasWorksheet));
      formDataToSend.append("worksheetPath", formData.worksheetPath.trim());

      /* ===== ADDITION ===== */
      formDataToSend.append("worksheets", JSON.stringify(worksheets));
      /* ==================== */

      if (formData.order.trim()) {
        formDataToSend.append("order", formData.order);
      }

      // VIDEO UPLOAD — UNCHANGED
      if (videoFile) {
        formDataToSend.append("video", videoFile);
      }

      await addLesson(formDataToSend).unwrap();

      alert("Lesson created successfully!");
      onClose();
    } catch (err: any) {
      alert(err?.data?.error || err?.message || "Failed to create lesson");
      console.error(err);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const disableSubmit = isLoading || uploadingWorksheet;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">
            Create New Lesson
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
            disabled={disableSubmit}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* Title */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Lesson Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-lg"
              disabled={disableSubmit}
            />
          </div>

          {/* Description */}
          <div className="mb-6">
            <input
              id="trix-description"
              type="hidden"
              ref={trixInputRef}
              value={formData.description}
              readOnly
            />
            <trix-editor
              ref={trixEditorRef as any}
              input="trix-description"
              className="trix-content border rounded-lg"
            />
          </div>

          {/* Video Upload — UNCHANGED */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">Video File</label>
            <input
              ref={videoInputRef}
              type="file"
              accept="video/*"
              onChange={handleVideoFileChange}
            />
          </div>

          {/* Worksheet */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <label className="flex items-center mb-3">
              <input
                type="checkbox"
                name="hasWorksheet"
                checked={formData.hasWorksheet}
                onChange={handleChange}
              />
              <span className="ml-2">This lesson has a worksheet</span>
            </label>

            {formData.hasWorksheet && (
              <>
                <input
                  type="text"
                  placeholder="Worksheet title"
                  value={worksheetTitle}
                  onChange={(e) => setWorksheetTitle(e.target.value)}
                  className="w-full mb-2 px-3 py-2 border rounded"
                />

                <input
                  ref={worksheetInputRef}
                  type="file"
                  onChange={handleWorksheetFile}
                />

                <ul className="mt-3 space-y-2">
                  {worksheets.map((w, i) => (
                    <li
                      key={i}
                      className="flex justify-between items-center bg-white px-3 py-2 border rounded"
                    >
                      <span>{w.title}</span>
                      <button
                        type="button"
                        onClick={() => removeWorksheet(i)}
                        className="text-red-600 text-sm"
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>

          {/* Order */}
          <div className="mb-6">
            <input
              type="number"
              name="order"
              value={formData.order}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-lg"
              placeholder="Order (optional)"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border px-6 py-3 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg"
              disabled={disableSubmit}
            >
              {isLoading ? "Creating..." : "Create Lesson"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
